import React, { useState, useRef, useEffect } from 'react';
import useUploadContext from 'hooks/useUploadContext';
import './index.scss';

const BareSetting = () => {
    const {
        bares,
        setBares,
        leftBoundary,
        rightBoundary,
        time,
        setManualCurrentTimeMusic,
    } = useUploadContext();

    const [draggingIndex, setDraggingIndex] = useState(null);
    const [changeTime, setChangeTime] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [parts, setParts] = useState([]);
    const waveRef = useRef(null);
    const posRef = useRef(0);
    const lastTapRef = useRef(null);

    const updatedTime = rightBoundary - leftBoundary;

    useEffect(() => {
        const filteredBares = bares.filter((bare) => (bare >= leftBoundary && bare <= rightBoundary));
        if (!filteredBares.includes(0)) filteredBares.unshift(0);
        if (!filteredBares.includes(rightBoundary)) filteredBares.push(rightBoundary);
        setParts(filteredBares);
    }, [bares, leftBoundary, rightBoundary]);

    useEffect(() => {
        if (!changeTime) {
            setCurrentTime(time);
        }
    }, [time, changeTime]);

    const updateBares = () => {
        const filteredBares = bares.filter((bare) => (bare < leftBoundary && bare > rightBoundary));
        const uniqueArray = [...new Set([...parts, ...filteredBares])];
        const sortedArray = uniqueArray.sort((a, b) => a - b);
        setBares(sortedArray);
    };

    const handlePointerDown = (event) => {
        const clientX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX;
        const element = event.target;
        const bareIndex = element.getAttribute('data');
        if (!bareIndex) {
            setChangeTime(true);
        } else {
            setDraggingIndex(Number(bareIndex));
        }
        posRef.current = clientX;
    };

    const handlePointerMove = (event) => {
        const clientX = event.type === 'touchmove' ? event.touches[0].clientX : event.clientX;
        const waveRect = waveRef.current.getBoundingClientRect();
        const diffX = clientX - posRef.current;
        const diffTime = (diffX / waveRect.width) * updatedTime;
        if (draggingIndex !== null) {
            setParts((prevParts) => {
                const newParts = [...prevParts];
                const currentRight = newParts[draggingIndex] + diffTime;
                newParts[draggingIndex] = currentRight;

                if (currentRight >= newParts[draggingIndex + 1]) {
                    newParts.splice(draggingIndex + 1, 1);
                } else if (currentRight <= newParts[draggingIndex - 1]) {
                    newParts.splice(draggingIndex -1, 1);
                    setDraggingIndex(draggingIndex -1);
                }

                const uniqueArray = [...new Set(newParts)];
                const sortedArray = uniqueArray.sort((a, b) => a - b);

                return sortedArray;
            });
        } else if (changeTime) {
            setCurrentTime((pre) => pre + diffTime);
            setChangeTime(true);
        }
        posRef.current = clientX;
    };

    const handlePointerUp = (event) => {
        const clientX = event.type === 'touchend' ? event.changedTouches[0].clientX : event.clientX;
        if (draggingIndex !== null) {
            setDraggingIndex(null);
            updateBares();
        } else if (changeTime) {
            const waveRect = waveRef.current.getBoundingClientRect();
            const newTime = ((clientX - waveRect.left) / waveRect.width) * updatedTime + leftBoundary;
            setManualCurrentTimeMusic(newTime);
            setChangeTime(false);
        }
    };

    const handleDeleteBare = (i) => {
        const newParts = [...parts];
        newParts.splice(i, 1);
        setParts(newParts);
        updateBares();
    };

    const handleAddBare = () => {
        const waveRect = waveRef.current.getBoundingClientRect();
        const newBare = ((posRef.current- waveRect.left) / waveRect.width) * updatedTime + leftBoundary;
        const selectedBare = bares.findIndex((bare) => (bare === newBare));
        if (selectedBare === -1) {
            const filteredBares = bares.filter((bare) => (bare < leftBoundary && bare > rightBoundary));
            const uniqueArray = [...new Set([...parts, newBare, ...filteredBares])];
            const sortedArray = uniqueArray.sort((a, b) => a - b);
            setBares(sortedArray);
        }
    };

    const handleDoubleClick = (event) => {
        const element = event.target;
        const bareNumber = element.getAttribute('data');
        if(bareNumber) {
            handleDeleteBare(Number(bareNumber))
        } else {
            handleAddBare()
        }
    };

    const handleDoubleTap = (event) => {
        const currentTime = new Date().getTime();
        const tapGap = 500;
        if (lastTapRef.current && currentTime - lastTapRef.current < tapGap) {
            handleDoubleClick(event);
        }
        lastTapRef.current = currentTime;
    };

    const getWidth = (i) => {
        if (i === 1) {
            return parts[i] - leftBoundary;
        } else {
            return parts[i] - parts[i - 1];
        }
    };

    const getClass = (i) => {
        if (draggingIndex === null) {
            return '';
        }
        if (i === draggingIndex) {
            return 'left-active';
        }
        if (i === draggingIndex + 1) {
            return 'right-active'
        }
    };

    return (
        <div
            className="bare-container"
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
            onMouseMove={handlePointerMove}
            onTouchMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchEnd={handlePointerUp}
            onTouchCancel={handlePointerUp}
            onDoubleClick={handleDoubleClick}
            onTouchEndCapture={handleDoubleTap}
            ref={waveRef}

        >
            {!!updatedTime && parts.map((b, i) => (
                (i !== 0 && i !== parts.length - 1) &&
                <div
                    key={i}
                    className={`right-side ${i === draggingIndex ? 'active' : ''}`}
                    style={{ left: `${((b - leftBoundary) / updatedTime) * 100}%` }}
                    data={i}
                ></div>
            ))}
            {!!updatedTime && parts.map((_, i) => (
                (i !== 0) &&
                <div
                    key={i}
                    className={`bare ${!bares.length ? '' : i % 2 !== 0 ? 'even' : 'odd'} ${getClass(i)}`}
                    style={{
                        width: `${(getWidth(i) / updatedTime) * 100}%`,
                    }}
                >
                </div>
            ))}
            {!!updatedTime && (
                <div
                    className="time-indicator"
                    style={{ left: `${((currentTime - leftBoundary) / updatedTime) * 100}%` }}
                ></div>
            )}
        </div>
    );
};

export default BareSetting;
